import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { useChat } from "@/components/layouts/Chat/ChatContext";
import { useAPIMutation } from "@/services/api/hook";

// Retry is HTTP in, WebSocket out: the POST returns 202 and the regenerated
// version arrives on the normal message broadcast, keyed to the same message id.
// Nothing about the result comes back in the response, so the card stays in its
// generating state until the broadcast carries a version this client has not
// seen yet.
//
// Keyed by message id — that is what the endpoint addresses, and it is also what
// identifies the card in the chat list. The draft id is only meaningful to the
// backend.

interface RegenerateResponse {
  requestId: string;
}

interface RegenerateRequest {
  pathParams: { messageId: string };
  data: { guidance?: string };
}

// Upper bound on how long the card may show the generating state. The result
// arrives out-of-band, so a regeneration that dies server-side has nothing to
// clear the spinner; without this the card spins for the life of the session.
const REGENERATE_TIMEOUT_MS = 180_000;

export interface DraftEmailRegenerate {
  regenerateDraft: (messageId: string, guidance?: string) => void;
  // Message ids whose card has a regeneration in flight. Retry is disabled for
  // these — the backend cannot serialize two appends to the same card.
  regeneratingMessageIds: Set<string>;
}

export const useDraftEmailRegenerate = (): DraftEmailRegenerate => {
  const { t } = useTranslation();
  const { messages } = useChat();
  // messageId -> how many versions the card had when Retry was pressed.
  const [pendingVersionCounts, setPendingVersionCounts] = useState<
    Record<string, number>
  >({});
  const timeoutsRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  const clearPending = useCallback((messageId: string) => {
    const timeout = timeoutsRef.current[messageId];
    if (timeout) {
      clearTimeout(timeout);
      delete timeoutsRef.current[messageId];
    }
    setPendingVersionCounts((previous) => {
      if (!(messageId in previous)) return previous;
      const next = { ...previous };
      delete next[messageId];
      return next;
    });
  }, []);

  const { mutate } = useAPIMutation<RegenerateRequest, RegenerateResponse>(
    "messaging/draftRegenerate",
    { method: "POST" }
  );
  const mutateRef = useRef(mutate);
  mutateRef.current = mutate;

  // Drop the pending flag once the regenerated version lands over the WebSocket.
  useEffect(() => {
    const arrived = Object.keys(pendingVersionCounts).filter((messageId) => {
      const message = messages.find((candidate) => candidate.id === messageId);
      if (!message) return true;
      return (
        (message.emailDraft?.versions.length ?? 0) >
        pendingVersionCounts[messageId]
      );
    });
    arrived.forEach(clearPending);
  }, [messages, pendingVersionCounts, clearPending]);

  useEffect(
    () => () => {
      Object.values(timeoutsRef.current).forEach(clearTimeout);
      timeoutsRef.current = {};
    },
    []
  );

  // Read through a ref: the callback is handed to memoized chat balloons, and a
  // new identity on every incoming message would defeat their comparison.
  const messagesRef = useRef(messages);
  messagesRef.current = messages;

  const regenerateDraft = useCallback(
    (messageId: string, guidance?: string) => {
      const message = messagesRef.current.find(
        (candidate) => candidate.id === messageId
      );
      const versionCount = message?.emailDraft?.versions.length ?? 0;
      setPendingVersionCounts((previous) => ({
        ...previous,
        [messageId]: versionCount
      }));
      // Retry is disabled through regeneratingMessageIds, but that gate is a
      // committed state update — two submits in the same tick both land here,
      // and overwriting the handle would strand the first timer beyond the
      // reach of clearPending.
      const inFlightTimeout = timeoutsRef.current[messageId];
      if (inFlightTimeout) {
        clearTimeout(inFlightTimeout);
      }
      timeoutsRef.current[messageId] = setTimeout(() => {
        clearPending(messageId);
        toast.error(t("draftEmail.regenerateError"));
      }, REGENERATE_TIMEOUT_MS);

      mutateRef.current(
        {
          pathParams: { messageId },
          data: guidance ? { guidance } : {}
        },
        {
          // 202 only means the retry was dispatched; the card keeps waiting for
          // the broadcast.
          onSuccess: () => {},
          onError: () => {
            clearPending(messageId);
            toast.error(t("draftEmail.regenerateError"));
          }
        }
      );
    },
    [clearPending, t]
  );

  const regeneratingMessageIds = useMemo(
    () => new Set(Object.keys(pendingVersionCounts)),
    [pendingVersionCounts]
  );

  return { regenerateDraft, regeneratingMessageIds };
};
